// Route guard for signed-in pages:
//  - 401 from /api/me -> /sign-in?next=<where you were> (or the `signIn` page given)
//  - email not confirmed -> /verify-email
//  - organisation waiting for payment details -> /welcome/payment
//  - no active organisation -> /welcome/organisation (unless allowNoOrg)
// Pages inside read the signed-in person with useCurrentMe().
import { createContext, useContext, type ReactNode } from "react";
import { Navigate, Outlet, useLocation, useOutletContext } from "react-router";
import type { Me } from "@convenor/shared";
import { isSignedOutError, useMe } from "../api/session";
import { Loading, PageError } from "../ui/QueryStates";
import { PublicShell } from "./Shells";
import { TeamOnlyPage, teamOnlyHome } from "./TeamOnlyPage";

export const MeContext = createContext<Me | null>(null);

/** The pages someone may open while their organisation waits for payment details. */
const PAYMENT_OPEN = ["/welcome/payment", "/sign-out", "/account"];

export function RequireAuth({ allowNoOrg = false, signIn = "/sign-in", children }: { allowNoOrg?: boolean; signIn?: string; children?: ReactNode }) {
  const me = useMe();
  const location = useLocation();

  if (me.isPending) {
    return (
      <PublicShell>
        <Loading />
      </PublicShell>
    );
  }
  if (me.isError) {
    if (isSignedOutError(me.error)) {
      const next = `${location.pathname}${location.search}`;
      return <Navigate to={next === "/" ? signIn : `${signIn}?next=${encodeURIComponent(next)}`} replace />;
    }
    return (
      <PublicShell>
        <PageError error={me.error} />
      </PublicShell>
    );
  }
  // Someone who has not typed the email code yet goes back to it: later forms cannot be saved.
  const user = me.data.user as Me["user"] & { emailVerified?: boolean };
  if (user.emailVerified === false && location.pathname !== "/verify-email") {
    return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email)}`} replace />;
  }
  // An organisation waiting for payment details: only that screen (and signing out, the account page).
  const trial = (me.data.org as { trial?: { phase?: string } | null } | null)?.trial;
  if (trial?.phase === "awaiting_payment" && !PAYMENT_OPEN.includes(location.pathname)) return <Navigate to="/welcome/payment" replace />;
  if (!me.data.org && !allowNoOrg) {
    // The funder's contract manager has no organisation: back to the shared project. An expert gets
    // "This page is for the project team" with the way back (EP-F8). Never "set up an organisation".
    const home = teamOnlyHome((me.data as { home?: string }).home);
    if (home?.startsWith("/cm/")) return <Navigate to={home} replace />;
    if (home) {
      return (
        <PublicShell>
          <TeamOnlyPage home={home} />
        </PublicShell>
      );
    }
    return <Navigate to="/welcome/organisation" replace />;
  }
  return <MeContext.Provider value={me.data}>{children ?? <Outlet context={me.data} />}</MeContext.Provider>;
}

/** The signed-in person, inside any page under <RequireAuth>. */
export function useCurrentMe(): Me {
  const fromContext = useContext(MeContext);
  const fromOutlet = useOutletContext<Me | undefined>();
  const me = fromContext ?? fromOutlet;
  if (!me) throw new Error("useCurrentMe() is only available under <RequireAuth>.");
  return me;
}
